
import gsap from 'gsap';

export default class Marquee {
  constructor(element, lenis) {
    this.lenis = lenis;
    this.container = element;
    this.marquee = this.container.querySelector('.marquee-content');
    
    if (!this.marquee || !this.container) return;
    
    this.group = this.marquee.querySelector('.marquee-group');
    if (!this.group) return;

    // Get speed and direction from data attributes or default
    const speedAttr = this.container.dataset.speed;
    this.baseSpeed = speedAttr ? parseFloat(speedAttr) : 0.8; 
    
    this.scrollSensitivity = 0.05; 
    this.currentSpeed = 0;
    this.x = 0;
    this.wrapWidth = 0;

    this.resizeObserver = new ResizeObserver(() => this.calculateWidth());
    this.calculateWidthBound = this.calculateWidth.bind(this);
    this.init();
  }

  init() {
    this.calculateWidth();
    this.resizeObserver.observe(this.marquee);
    
    // Set initial position: 0 is always safe for start
    this.x = 0;

    this.bindEvents();
    
    // Use GSAP Ticker for better performance and sync
    this.tickerFn = () => this.animate();
    gsap.ticker.add(this.tickerFn);
    
    // Re-verify width after everything is potentially loaded
    window.addEventListener('load', this.calculateWidthBound);
    setTimeout(() => this.calculateWidth(), 1000);

    gsap.set(this.marquee, { opacity: 1, x: this.x });
  }

  calculateWidth() {
    if (!this.group) return;
    // GetBoundingClientRect is necessary for sub-pixel precision in modern browsers
    const groupWidth = this.group.getBoundingClientRect().width;
    if (groupWidth > 0) {
        this.wrapWidth = groupWidth;
    } else {
        // Fallback to scrollWidth if offset is 0
        this.wrapWidth = this.marquee.scrollWidth / 2;
    }
  }

  bindEvents() {
    this.container.addEventListener('mouseenter', () => {
      this.isHovered = true;
    });

    this.container.addEventListener('mouseleave', () => {
      this.isHovered = false;
    });
  }

  animate() {
    if (!this.wrapWidth || this.wrapWidth === 0) {
        this.calculateWidth();
        return;
    }

    const scrollVelocity = this.lenis ? this.lenis.velocity : 0;
    const velocityInfluence = scrollVelocity * this.scrollSensitivity;
    
    let targetSpeed = this.baseSpeed + velocityInfluence;
    
    // Clamp extreme velocity to keep things readable
    if (Math.abs(scrollVelocity) > 2.0) {
         if (scrollVelocity > 0) targetSpeed = Math.abs(this.baseSpeed) + Math.abs(velocityInfluence);
         else targetSpeed = -(Math.abs(this.baseSpeed) + Math.abs(velocityInfluence));
    }
    
    if (this.isHovered) targetSpeed = 0;

    this.currentSpeed += (targetSpeed - this.currentSpeed) * 0.1;
    this.x += this.currentSpeed;
    
    // --- Pixel Perfect Continuous Loop ---
    // If moving left (x decreasing), when x < -wrapWidth, we jump forward by wrapWidth.
    // If moving right (x increasing), when x > 0, we jump back by wrapWidth.
    // This assumes Group 1 and Group 2 are identical and placed side-by-side.
    
    if (this.x < -this.wrapWidth) {
        this.x += this.wrapWidth;
    } else if (this.x > 0) {
        this.x -= this.wrapWidth;
    }
    
    gsap.set(this.marquee, { x: this.x, force3D: true });
  }

  destroy() {
    gsap.ticker.remove(this.tickerFn);
    this.resizeObserver.disconnect();
    this.resizeObserver.disconnect();
    window.removeEventListener('load', this.calculateWidthBound);
  }
}
